// File: src/components/Charts/CountryChoroplethMap.jsx

import React, { memo, useMemo } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { scaleLinear } from 'd3-scale';

// --- Language to Primary Country ISO-2 Mapping ---
// This map associates a language code (ISO 639-1) with a primary country's ISO 3166-1 alpha-2 (ISO2) code.
// You can expand/adjust this mapping based on which country you want to represent a given language.
export const languageToCountryMap = {
  "en": "US", // English -> United States (can also be GB, AU, CA, IE, NZ, etc.)
  "es": "MX", // Spanish -> Mexico (or ES, AR, CO, PE, CL, etc.)
  "fr": "FR", // French -> France (or CA, BE, CH, etc.)
  "de": "DE", // German -> Germany (or AT, CH)
  "it": "IT", // Italian -> Italy
  "pt": "BR", // Portuguese -> Brazil (or PT)
  "ru": "RU", // Russian -> Russia
  "zh": "CN", // Chinese -> China (Mandarin, simplified)
  "ja": "JP", // Japanese -> Japan
  "ko": "KR", // Korean -> South Korea
  "ar": "SA", // Arabic -> Saudi Arabia (representing a major Arabic-speaking country)
  "hi": "IN", // Hindi -> India
  "bn": "BD", // Bengali -> Bangladesh (also spoken in India)
  "th": "TH", // Thai -> Thailand
  "vi": "VN", // Vietnamese -> Vietnam
  "id": "ID", // Indonesian -> Indonesia
  "ms": "MY", // Malay -> Malaysia (also Brunei, Singapore)
  "tr": "TR", // Turkish -> Turkey
  "pl": "PL", // Polish -> Poland
  "uk": "UA", // Ukrainian -> Ukraine
  "nl": "NL", // Dutch -> Netherlands (or BE for Flemish)
  "sv": "SE", // Swedish -> Sweden
  "no": "NO", // Norwegian -> Norway
  "da": "DK", // Danish -> Denmark
  "fi": "FI", // Finnish -> Finland
  "he": "IL", // Hebrew -> Israel
  "fa": "IR", // Persian (Farsi) -> Iran (also AF for Dari, TJ for Tajik)
  "ur": "PK", // Urdu -> Pakistan (also spoken in India)
  "af": "ZA", // Afrikaans -> South Africa
  "sw": "KE", // Swahili -> Kenya (also TZ, UG, CD)
  "tl": "PH", // Filipino (Tagalog) -> Philippines
  "el": "GR", // Greek -> Greece (or CY)

  // --- Added more languages and countries (Duplicates removed) ---
  "mr": "IN", // Marathi -> India
  "te": "IN", // Telugu -> India
  "ta": "IN", // Tamil -> India (also LK, SG)
  "gu": "IN", // Gujarati -> India
  "kn": "IN", // Kannada -> India
  "ml": "IN", // Malayalam -> India
  "pa": "IN", // Punjabi -> India (or PK)
  "si": "LK", // Sinhala -> Sri Lanka
  "ne": "NP", // Nepali -> Nepal
  "km": "KH", // Khmer -> Cambodia
  "lo": "LA", // Lao -> Laos
  "my": "MM", // Burmese -> Myanmar
  "am": "ET", // Amharic -> Ethiopia
  "ha": "NG", // Hausa -> Nigeria (also Niger, Ghana)
  "so": "SO", // Somali -> Somalia
  "ig": "NG", // Igbo -> Nigeria
  "yo": "NG", // Yoruba -> Nigeria
  "zu": "ZA", // Zulu -> South Africa
  "uz": "UZ", // Uzbek -> Uzbekistan
  "az": "AZ", // Azerbaijani -> Azerbaijan
  "kk": "KZ", // Kazakh -> Kazakhstan
  "ky": "KG", // Kyrgyz -> Kyrgyzstan
  "cs": "CZ", // Czech -> Czech Republic
  "hu": "HU", // Hungarian -> Hungary
  "ro": "RO", // Romanian -> Romania
  "sk": "SK", // Slovak -> Slovakia
  "hr": "HR", // Croatian -> Croatia (also BA, RS)
  "sr": "RS", // Serbian -> Serbia (also BA, ME)
  "bg": "BG", // Bulgarian -> Bulgaria
  "lt": "LT", // Lithuanian -> Lithuania
  "lv": "LV", // Latvian -> Latvia
  "et": "EE", // Estonian -> Estonia
  "is": "IS", // Icelandic -> Iceland
  "ga": "IE", // Irish -> Ireland
  "hy": "AM", // Armenian -> Armenia
  "ka": "GE", // Georgian -> Georgia
  "sq": "AL", // Albanian -> Albania (also XK, MK)
  "bs": "BA", // Bosnian -> Bosnia and Herzegovina
  "mk": "MK", // Macedonian -> North Macedonia
  "tg": "TJ", // Tajik -> Tajikistan
  "ug": "CN", // Uyghur -> China (Xinjiang)
  "mn": "MN", // Mongolian -> Mongolia
  "dz": "BT", // Dzongkha -> Bhutan
  // ... feel free to add more as needed
};

// 1) ISO-2 → numeric TopoJSON id map (stringified).
const iso2ToNumeric = {
  AF: "004", AL: "008", DZ: "012", AS: "016", AD: "020", AO: "024", AI: "660", AQ: "010", AG: "028", AR: "032",
  AM: "051", AW: "533", AU: "036", AT: "040", AZ: "031", BS: "044", BH: "048", BD: "050", BB: "052", BY: "112",
  BE: "056", BZ: "084", BJ: "204", BM: "060", BT: "064", BO: "068", BA: "070", BW: "072", BR: "076", IO: "086",
  BN: "096", BG: "100", BF: "854", BI: "108", CV: "132", KH: "116", CM: "120", CA: "124", KY: "136", CF: "140",
  TD: "148", CL: "152", CN: "156", CO: "170", KM: "174", CG: "178", CD: "180", CK: "184", CR: "188", CI: "384",
  HR: "191", CU: "192", CY: "196", CZ: "203", DK: "208", DJ: "262", DM: "212", DO: "214", EC: "218", EG: "818",
  SV: "222", GQ: "226", ER: "232", EE: "233", SZ: "748", ET: "231", FK: "238", FO: "234", FJ: "242", FI: "246",
  FR: "250", GF: "254", PF: "258", GA: "266", GM: "270", GE: "268", DE: "276", GH: "288", GR: "300", GL: "304",
  GD: "308", GP: "312", GU: "316", GT: "320", GN: "324", GW: "624", GY: "328", HT: "332", HN: "340", HK: "344",
  HU: "348", IS: "352", IN: "356", ID: "360", IR: "364", IQ: "368", IE: "372", IM: "833", IL: "376", IT: "380",
  JM: "388", JP: "392", JO: "400", KZ: "398", KE: "404", KI: "296", KP: "408", KR: "410", KW: "414", KG: "417",
  LA: "418", LV: "428", LB: "422", LS: "426", LR: "430", LY: "434", LI: "438", LT: "440", LU: "442", MO: "446",
  MG: "450", MW: "454", MY: "458", MV: "462", ML: "466", MT: "470", MH: "584", MQ: "474", MR: "478", MU: "480",
  YT: "175", MX: "484", FM: "583", MD: "498", MC: "492", MN: "496", ME: "499", MS: "500", MA: "504", MZ: "508",
  MM: "104", NA: "516", NR: "520", NP: "524", NL: "528", NC: "540", NZ: "554", NI: "558", NE: "562", NG: "566",
  NU: "570", NF: "574", MK: "807", MP: "580", NO: "578", OM: "512", PK: "586", PW: "585", PS: "275", PA: "591",
  PG: "598", // Papua New Guinea (Changed from 558 to 598 for more common mapping)
  PY: "600", PE: "604", PH: "608", PN: "612", PL: "616", PT: "620", PR: "630", QA: "634", RE: "638", RO: "642",
  RU: "643", RW: "646", BL: "652", SH: "654", KN: "659", LC: "662", MF: "663", PM: "666", VC: "670", WS: "882",
  SM: "674", ST: "678", SA: "682", SN: "686", RS: "688", SC: "690", SL: "694", SG: "702", SX: "534", SK: "703",
  SI: "705", SB: "090", SO: "706", ZA: "710", SS: "728", ES: "724", LK: "144", SD: "729", SR: "740", SJ: "744",
  SE: "752", CH: "756", SY: "760", TW: "158", TJ: "762", TZ: "834", TH: "764", TL: "626", TG: "768", TK: "772",
  TO: "776", TT: "780", TN: "788", TR: "792", TM: "795", TC: "796", TV: "798", UG: "800", UA: "804", AE: "784",
  GB: "826", US: "840", UY: "858", UZ: "860", VU: "548", VE: "862", VN: "704", VG: "092", VI: "850", WF: "876",
  EH: "732", YE: "887", ZM: "894", ZW: "716",
};

// Invert it for lookup: numeric → ISO-2
const numericToIso2 = Object.fromEntries(
  Object.entries(iso2ToNumeric).map(([iso2, num]) => [num, iso2])
);

const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

// Assume countryCounts prop now actually contains language counts like { "en": 100, "ar": 50 }
const CountryChoroplethMap = ({ countryCounts: languageCounts }) => { // Renamed prop for clarity

  // --- Convert language counts to country counts for the map ---
  const mappedCountryCounts = useMemo(() => {
    const counts = {};
    for (const langCode in languageCounts) {
      if (languageCounts.hasOwnProperty(langCode)) {
        const countryIso2 = languageToCountryMap[langCode];
        if (countryIso2) {
          counts[countryIso2] = (counts[countryIso2] || 0) + languageCounts[langCode];
        }
      }
    }
    return counts;
  }, [languageCounts]); // Recalculate if languageCounts changes

  const counts = Object.values(mappedCountryCounts); // Use mappedCountryCounts here
  const maxCount = counts.length ? Math.max(...counts) : 1;
  const colorScale = scaleLinear()
    .domain([0, maxCount])
    .range(['#EDE7F6', '#6A0DAD']);

  return (
    <ComposableMap projectionConfig={{ scale: 147 }}>
      <Geographies geography={GEO_URL}>
        {({ geographies }) => {
          return geographies.map(geo => {
            const numericId = String(geo.id);
            const iso2 = numericToIso2[numericId];

            // Lookup sessions using the newly derived mappedCountryCounts
            const sessions = iso2 ? (mappedCountryCounts[iso2] || 0) : 0;
            const fill = sessions ? colorScale(sessions) : '#f0f0f0';

            return (
              <Geography
                key={geo.rsmKey}
                geography={geo}
                fill={fill}
                stroke="#DDD"
                style={{
                  default: { outline: 'none' },
                  hover:   { fill: '#00A8A9', outline: 'none' },
                  pressed: { outline: 'none' },
                }}
              />
            );
          });
        }}
      </Geographies>
    </ComposableMap>
  );
};

export default memo(CountryChoroplethMap);